import { useEffect, useMemo, useState } from 'react';

import { useEmployerReactivationStore } from '@/modules/employer/components/EmployerReactivation/stores/useEmployerReactivation.store';

const ERROR_MESSAGES = {
  NO_BANK_ACCOUNTS: 'Cannot continue until bank accounts are configured',
  FIELDS_REQUIRED: 'Please correct the highlighted errors to reactivate the Employer',
};

export const useEmployerReactivationValidation = () => {
  const hasBankAccounts = useEmployerReactivationStore((state) => state.hasBankAccounts);
  const payrollValue = useEmployerReactivationStore((state) => state.payrollValue);
  const fundingValue = useEmployerReactivationStore((state) => state.fundingValue);
  const reactivatePlans = useEmployerReactivationStore((state) => state.reactivatePlans);
  const selectedPlans = useEmployerReactivationStore((state) => state.selectedPlans);
  const [errors, setErrors] = useState<string[]>([]);
  const [hasFieldErrors, setHasFieldErrors] = useState(false);

  const hasInvalidPlans = useMemo(
    () => reactivatePlans && selectedPlans.some((p) => p.isPlanLevelFunding && !p.external_bank_account_id),
    [reactivatePlans, selectedPlans]
  );

  useEffect(() => {
    if (errors.length > 0) {
      const shouldClearBankError = hasBankAccounts && errors.includes(ERROR_MESSAGES.NO_BANK_ACCOUNTS);
      const shouldClearFieldErrors = hasBankAccounts && payrollValue && fundingValue && !hasInvalidPlans && hasFieldErrors;
      
      if (shouldClearBankError || shouldClearFieldErrors) {
        setErrors([]);
        setHasFieldErrors(false);
      }
    }
  }, [hasBankAccounts, payrollValue, fundingValue, hasInvalidPlans, errors, hasFieldErrors]);

  const validate = (): boolean => {
    const newErrors: string[] = [];
    let fieldsHaveErrors = false;

    if (!hasBankAccounts) {
      newErrors.push(ERROR_MESSAGES.NO_BANK_ACCOUNTS);
    }

    if (hasBankAccounts && (!payrollValue || !fundingValue || hasInvalidPlans)) {
      newErrors.push(ERROR_MESSAGES.FIELDS_REQUIRED);
      fieldsHaveErrors = true;
    }

    setErrors(newErrors);
    setHasFieldErrors(fieldsHaveErrors);
    return newErrors.length === 0;
  };

  const clearErrors = () => {
    setErrors([]);
  };

  const hasErrors = useMemo(() => errors.length > 0, [errors]);

  return {
    errors,
    hasErrors,
    hasFieldErrors,
    validate,
    clearErrors,
  };
};
